import { ChangeDetectorRef, Component, inject, Input } from '@angular/core';
import { CommonModule, } from '@angular/common';
import { WordElement } from '@models/WordElement';
import { GlossSelectorLineComponent } from '../gloss-selector-line/gloss-selector-line.component';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Gloss } from '@models/Gloss';
import { Annotation, NullAnnotation } from '@models/Annotation';
import { SortByVotesPipe } from "../../sort-by-votes.pipe";
import { Verse } from '@models/Verse';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { GlossDialogComponent, GlossDialogInput, GlossDialogOutput } from '../gloss-dialog/gloss-dialog.component';
import { SynchronizerService } from '../../synchronizer.service';
import { AnnotationType } from '@models/AnnotationJsonObject';
import { ApiService } from '../../api.service';
import { Voice } from '@models/Voice';
import { ProjectTextStyleDirective } from 'src/app/project-text-style.directive';
import { VoiceInEnglishPipe } from 'src/app/voice-in-english.pipe';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-gloss-selector',
  templateUrl: './gloss-selector.component.html',
  styleUrl: './gloss-selector.component.scss',
  imports: [FormsModule, MatFormFieldModule, MatInputModule, FormsModule, CommonModule, GlossSelectorLineComponent, MatInputModule, MatIconModule, SortByVotesPipe, MatMenuModule, MatButtonModule, MatIconModule, ProjectTextStyleDirective, VoiceInEnglishPipe, MatTooltipModule]
})
export class GlossSelectorComponent {
  @Input() element!: WordElement;
  @Input() verse!: Verse;
  newGloss: string = "";
  readonly dialog = inject(MatDialog);
  public expandMatchingGlosses = false;

  public nonMatchingGlossSuggestions = new Map<Voice, Gloss[]>();

  constructor(
    private cd: ChangeDetectorRef,
    public synchronizerService: SynchronizerService,
    private api: ApiService) {
  }

  ngOnInit() {
    this.element.glossSuggestions
      .filter((gs) => gs.voice != this.element.voice)
      .forEach((gs) => {
        if (!this.nonMatchingGlossSuggestions.has(gs.voice)) {
          this.nonMatchingGlossSuggestions.set(gs.voice, []);
        }
        this.nonMatchingGlossSuggestions.get(gs.voice)!.push(gs);
      });
  }

  layoutDirectionLtr(): boolean {
    return this.synchronizerService?.currentProject?.layout_direction === "ltr";
  }

  openDialog(type: AnnotationType, gloss?: Gloss): void {
    const data: GlossDialogInput = { type: type, newGloss: true, gloss: gloss };
    const dialogRef = this.dialog.open<GlossDialogComponent, GlossDialogInput, GlossDialogOutput>(GlossDialogComponent, {
      data: data,
    });

    dialogRef.afterClosed().subscribe((output) => {
      if (output !== undefined && output.action === 'create') {
        output.annotation.voice = this.element.voice;
        this.addNewGloss(output.annotation);
      }
    });
  }

  addNewGloss(annotation: Annotation) {
    if (this.synchronizerService.currentUser) {
      const user_id = this.synchronizerService.currentUser.user_id;
      this.verse.addGlossForLexId(this.element.lex_id, annotation, user_id);
      const glossWithVote = Gloss.newGloss(annotation, this.element.location(), user_id);
      this.element.setVote(glossWithVote, user_id);
      this.refresh();
    }
  }

  addNewSimpleGloss() {
    this.openDialog('word');
  }

  addNewMarkdownGloss() {
    this.openDialog('markdown');
  }

  addWordPlusMarkdownGloss() {
    this.openDialog('wordplusmarkdown');
  }

  addEmptyGloss() {
    this.addNewGloss(new NullAnnotation(-1));
  }

  refresh() {
    this.cd.detectChanges();
  }

  processGlossAction(action: GlossDialogOutput) {
    if (action.action === 'delete') {
      this.deleteGloss(action.gloss);
    } else if (action.action === 'edit') {
      this.editGloss(action.gloss);
    }
  }

  async deleteGloss(gloss: Gloss) {
    /// if gloss_id is -1, it means this is a new gloss that has not been saved to the server yet
    if (gloss.gloss_id !== -1) {
      /// save whatever existing data there are
      await this.synchronizerService.saveVerseData();
      /// remove the gloss from the server
      await this.api.removeGloss(gloss.gloss_id);
      /// get the verse data from the server again
      this.synchronizerService.forceVerseReloadWithoutSendingData();
    }
  }

  async editGloss(gloss: Gloss) {
    /// if gloss_id is -1, it means this is a new gloss that has not been saved to the server yet
    if (gloss.gloss_id !== -1) {
      /// save whatever existing data there are
      await this.synchronizerService.saveVerseData();
      /// remove the gloss from the server
      await this.api.editGloss(gloss);
      /// get the verse data from the server again
      this.synchronizerService.forceVerseReloadWithoutSendingData();
    }
  }

  get suggestGlossesBasedOnLexicalItemOnly(): boolean {
    return this.synchronizerService.currentProject?.glossSuggestionMode == 'byLexicalItem';
  }

  get matchingGlossSuggestions(): Gloss[] {
    return this.element.glossSuggestions.filter((gs) => gs.voice == this.element.voice);
  }

  get glossSuggestions(): Gloss[] {
    if (this.suggestGlossesBasedOnLexicalItemOnly) {
      return this.element.glossSuggestions;
    } else {
      return this.matchingGlossSuggestions;
    }
  }

  nonMatchingDoubleClick(g: Gloss) {
    this.openDialog('word', g);
  }

  get displayMatchingGlosses(): boolean {
    return !this.suggestGlossesBasedOnLexicalItemOnly && this.nonMatchingGlossSuggestions.size > 0;
  }

}
